/**
 * ScreenshotCardComponent
 *
 * Single row in the ScreenshotPanel list. Pure presentational — emits user
 * intent up to the panel which routes to the host.
 *
 * Layout: optional checkbox + 96x64 thumbnail (with annotation/share badges) +
 * content column (caption / dims+age / linked-measurement chip) + 4 action
 * icon-buttons (Locate / Share / Annotate / Delete).
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

import { Screenshot } from '../../screenshots.types';

@Component({
  standalone: false,
  selector: 'app-screenshot-card',
  templateUrl: './screenshot-card.component.html',
  styleUrls: ['./screenshot-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreenshotCardComponent {
  @Input() screenshot!: Screenshot;
  @Input() selectMode = false;
  @Input() selected = false;
  /** When true, card glows to indicate it belongs to the currently-viewed PDF page. */
  @Input() onCurrentPage = false;
  /**
   * Id of the screenshot the host is currently "locating" (persistent ring on the
   * canvas). When it matches this card's screenshot id, the locate icon turns green
   * to signal the toggle is active.
   */
  @Input() activeLocateScreenshotId: string | null = null;

  get isLocating(): boolean {
    return !!this.activeLocateScreenshotId && this.screenshot?.id === this.activeLocateScreenshotId;
  }

  @Output() locate = new EventEmitter<Screenshot>();
  @Output() annotate = new EventEmitter<Screenshot>();
  @Output() share = new EventEmitter<Screenshot>();
  @Output() delete = new EventEmitter<Screenshot>();
  @Output() copy = new EventEmitter<Screenshot>();
  @Output() selectToggle = new EventEmitter<Screenshot>();

  /** Card body click — in select mode toggles selection; otherwise opens annotator. */
  onCardClick(): void {
    if (this.selectMode) {
      this.selectToggle.emit(this.screenshot);
    } else {
      this.annotate.emit(this.screenshot);
    }
  }

  onLocate(event: Event): void {
    event.stopPropagation();
    this.locate.emit(this.screenshot);
  }

  onAnnotate(event: Event): void {
    event.stopPropagation();
    this.annotate.emit(this.screenshot);
  }

  onShare(event: Event): void {
    event.stopPropagation();
    this.share.emit(this.screenshot);
  }

  onDelete(event: Event): void {
    event.stopPropagation();
    this.delete.emit(this.screenshot);
  }

  onCopy(event: Event): void {
    event.stopPropagation();
    this.copy.emit(this.screenshot);
  }

  /** "5 min ago" / "2 hours ago" / "3 days ago" — small helper, no date-fns dep. */
  get ageLabel(): string {
    return this.formatAge(this.screenshot.createdAt);
  }

  get annotationCount(): number {
    return this.screenshot.annotations?.length ?? 0;
  }

  get shareCount(): number {
    return this.screenshot.shares?.length ?? 0;
  }

  get dimsLabel(): string {
    const r = this.screenshot.region;
    return `${Math.round(r.width)} × ${Math.round(r.height)}`;
  }

  private formatAge(iso: string): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const diff = Date.now() - then;
    const min = Math.floor(diff / 60_000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`;
    const wk = Math.floor(day / 7);
    if (wk < 4) return `${wk} wk ago`;
    return new Date(iso).toLocaleDateString();
  }
}
